import { useMemo, useState } from 'react';
import { Danger } from '@/components/icons';
import { AppDialogLayout, StepTrail } from '@/components/system';
import { Alert } from '@/components/ui/alert';
import { ReportMeasurementProvider } from '@/context/ReportMeasurementContext';
import { getPublisherResultTableEntries } from '@/lib/publisher-result-imports';
import { CONFIRM_STEP_LABEL, instrumentShortLabel } from './guided-review-model';
import {
    GuidedReviewFooter,
    NarrativeSection,
    WarningsAlert,
    type FileNavigation,
} from './guided-review-parts';
import { ExcludedTables, TableCard, TablePreview } from './guided-review-tables';
import type { NarrativeDecision, TrailStep } from './guided-review-model';
import type {
    PublisherResultImport,
    PublisherResultInstrument,
    PublisherResultTableEntry,
} from '@/types/publisher-result-import';
import type { ReportData } from '@/types/report';

/**
 * The guided review of one imported PDF, one instrument per step.
 *
 * The steps are the instruments themselves, in the order the pipeline read
 * them, and the last one is the read-through before approving. Each step holds
 * everything that instrument will put in the laudo — its tables, and under each
 * table the paragraph written about it — laid out in the order
 * `buildReportBlocks` writes them, so the step is a rehearsal of the block that
 * will land in the report.
 *
 * It used to be cut the other way: every table on one step, every AI paragraph
 * on the next. That asked the psychologist to sign off a sentence like
 * "percentil 80, alto" with the table that says so two screens behind, so the
 * only thing they could judge was whether the sentence sounded plausible —
 * which is exactly what the model was already doing. Under its own table the
 * paragraph can be checked instead of believed.
 *
 * Everything here has a default and nothing has to be answered: tables the
 * pipeline left out sit behind a disclosure on their instrument's step, and the
 * paragraph it wrote is already in — reversing either is one click. The screen
 * is a review, not a form.
 *
 * A PDF holding one instrument — which is most of them — has one step and no
 * trail at all. The wizard is for a battery export or a WISC protocol-plus-
 * analysis pair, where the instruments really are separate things to check.
 */

type DecisionState = Record<string, NarrativeDecision>;

type PublisherResultGuidedReviewProps = {
    record: PublisherResultImport;
    reportData?: ReportData;
    selectedTableIds: string[];
    onToggleTable: (tableId: string) => void;
    approving: boolean;
    cancelling: boolean;
    error?: string | null;
    onApprove: (input: {
        selectedTableIds: string[];
        tableNarratives: Array<{ table_id: string; narrative: string | null }>;
    }) => void;
    onDiscard: () => void;
    onBackToMenu?: () => void;
    onClose: () => void;
    fileNavigation?: FileNavigation;
};

const instrumentTitle = (instrument: PublisherResultInstrument) =>
    instrument.instrument_name?.trim() || 'Instrumento não identificado';

export function PublisherResultGuidedReview({
    record,
    reportData,
    selectedTableIds,
    onToggleTable,
    approving,
    cancelling,
    error,
    onApprove,
    onDiscard,
    onBackToMenu,
    onClose,
    fileNavigation,
}: PublisherResultGuidedReviewProps) {
    const [stepIndex, setStepIndex] = useState(0);
    const [decisions, setDecisions] = useState<DecisionState>({});

    const entries = useMemo(() => getPublisherResultTableEntries(record), [record]);
    const selected = useMemo(() => new Set(selectedTableIds), [selectedTableIds]);
    const includedEntries = entries.filter(({ table }) => selected.has(table.id));

    const instruments = useMemo(() => {
        const byId = new Map<
            string,
            { instrument: PublisherResultInstrument; entries: PublisherResultTableEntry[] }
        >();

        for (const entry of entries) {
            const group = byId.get(entry.instrument.id);
            if (group) {
                group.entries.push(entry);
            } else {
                byId.set(entry.instrument.id, {
                    instrument: entry.instrument,
                    entries: [entry],
                });
            }
        }

        return Array.from(byId.values());
    }, [entries]);

    // One step per instrument, then the read-through. An index past the last
    // instrument *is* the confirm step — there is nothing else a step could be,
    // so it needs no name of its own.
    //
    // One instrument has no read-through, because there is nothing to read
    // through: the single step already shows every table and every paragraph
    // that will land in the laudo, and a second screen repeating it is a click
    // that buys the psychologist nothing. Most publisher PDFs are this case.
    const isSingleInstrument = instruments.length === 1;
    const stepCount = isSingleInstrument ? 1 : instruments.length + 1;
    const safeIndex = Math.min(stepIndex, stepCount - 1);
    const currentGroup = instruments[safeIndex];
    const isFirstStep = safeIndex === 0;
    const isLastStep = safeIndex === stepCount - 1;

    const trail: TrailStep[] = [
        ...instruments.map(({ instrument }) => ({
            key: instrument.id,
            label: instrumentShortLabel(instrument.instrument_name),
        })),
        { key: 'confirm', label: CONFIRM_STEP_LABEL },
    ];

    const stepEntries = currentGroup?.entries ?? [];
    const stepIncluded = stepEntries.filter(({ table }) => selected.has(table.id));
    const stepExcluded = stepEntries.filter(({ table }) => !selected.has(table.id));

    // A PDF the pipeline read but found no result table in lands straight on
    // the confirm step, which has nothing to confirm.
    const hasNothingToAdd = entries.length === 0;

    const resolveNarrative = (tableId: string, original: string) => {
        const decision = decisions[tableId];
        if (!decision) return original;
        if (decision.kind === 'omit') return '';
        if (decision.kind === 'edited') return decision.text;
        return original;
    };

    const handleApprove = () => {
        onApprove({
            selectedTableIds: includedEntries.map(({ table }) => table.id),
            // Only the tables that were actually asked about carry a decision;
            // the rest keep whatever the pipeline wrote for them.
            tableNarratives: includedEntries
                .filter(({ table }) => Boolean(table.narrative?.trim()))
                .map(({ table }) => ({
                    table_id: table.id,
                    narrative:
                        decisions[table.id]?.kind === 'omit'
                            ? null
                            : resolveNarrative(table.id, table.narrative?.trim() ?? ''),
                })),
        });
    };

    const stepTitle = currentGroup
        ? instrumentTitle(currentGroup.instrument)
        : hasNothingToAdd
          ? 'Este arquivo não tem resultados para o laudo'
          : 'Confira o que será adicionado ao laudo';

    return (
        <AppDialogLayout
            title={stepTitle}
            bodyClassName="pt-2"
            header={
                stepCount > 1 ? <StepTrail steps={trail} currentIndex={safeIndex} /> : undefined
            }
            backAction={
                onBackToMenu ? { label: 'Voltar às opções', onClick: onBackToMenu } : undefined
            }
            closeAction={{ label: 'Fechar', onClick: onClose }}
            footer={
                <GuidedReviewFooter
                    approving={approving}
                    cancelling={cancelling}
                    canApprove={includedEntries.length > 0}
                    isFirstStep={isFirstStep}
                    isLastStep={isLastStep}
                    fileNavigation={fileNavigation}
                    onDiscard={onDiscard}
                    onBack={() => setStepIndex((index) => Math.max(0, index - 1))}
                    onContinue={() => setStepIndex((index) => index + 1)}
                    onApprove={handleApprove}
                />
            }
        >
            <div className="space-y-4">
                {error && (
                    <Alert variant="destructive" icon={<Danger className="size-4" />}>
                        {error}
                    </Alert>
                )}

                {currentGroup && (
                    <>
                        {/* The warnings are about the file, not about one
                            instrument, so they open the review and come back
                            on the last screen before approving. */}
                        {isFirstStep && <WarningsAlert warnings={record.warnings ?? []} />}

                        {stepIncluded.length === 0 && (
                            <Alert variant="warning">
                                Nada deste instrumento entra no laudo. Inclua uma tabela abaixo ou
                                siga para o próximo passo.
                            </Alert>
                        )}

                        <ReportMeasurementProvider>
                            <div className="space-y-6">
                                {stepIncluded.map((entry) => (
                                    <TableCard
                                        key={entry.table.id}
                                        entry={entry}
                                        record={record}
                                        reportData={reportData}
                                        onExclude={() => onToggleTable(entry.table.id)}
                                    >
                                        {entry.table.narrative?.trim() && (
                                            <NarrativeSection
                                                entry={entry}
                                                decision={decisions[entry.table.id]}
                                                onDecide={(decision) =>
                                                    setDecisions((current) => {
                                                        const next = { ...current };
                                                        if (decision) {
                                                            next[entry.table.id] = decision;
                                                        } else {
                                                            delete next[entry.table.id];
                                                        }
                                                        return next;
                                                    })
                                                }
                                            />
                                        )}
                                    </TableCard>
                                ))}
                            </div>
                        </ReportMeasurementProvider>

                        <ExcludedTables entries={stepExcluded} onInclude={onToggleTable} />
                    </>
                )}

                {!currentGroup && (
                    <>
                        <WarningsAlert warnings={record.warnings ?? []} />

                        {hasNothingToAdd && (
                            <Alert variant="warning" icon={<Danger className="size-4" />}>
                                Não há nada para adicionar ao laudo a partir deste arquivo. Descarte
                                a importação e envie o relatório de resultados da editora.
                            </Alert>
                        )}

                        {!hasNothingToAdd && includedEntries.length === 0 && (
                            <Alert variant="warning">
                                Nenhuma tabela está marcada para entrar no laudo. Volte e inclua ao
                                menos uma.
                            </Alert>
                        )}

                        {!hasNothingToAdd && (
                            <ReportMeasurementProvider>
                                <div className="space-y-6">
                                    {instruments.map(({ instrument, entries: group }) => {
                                        const included = group.filter(({ table }) =>
                                            selected.has(table.id),
                                        );
                                        if (included.length === 0) return null;

                                        return (
                                            <div key={instrument.id} className="space-y-3">
                                                <h4 className="text-sm font-semibold text-foreground">
                                                    {instrumentTitle(instrument)}
                                                </h4>

                                                {included.map((entry) => {
                                                    const narrative = resolveNarrative(
                                                        entry.table.id,
                                                        entry.table.narrative?.trim() ?? '',
                                                    );

                                                    return (
                                                        <div
                                                            key={entry.table.id}
                                                            className="space-y-3 rounded-2xl border border-border bg-card p-4"
                                                        >
                                                            <TablePreview
                                                                entry={entry}
                                                                record={record}
                                                                reportData={reportData}
                                                            />
                                                            {narrative && (
                                                                <p className="text-sm leading-relaxed text-foreground">
                                                                    {narrative}
                                                                </p>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            </ReportMeasurementProvider>
                        )}
                    </>
                )}
            </div>
        </AppDialogLayout>
    );
}
